


























































































import { api } from '@/api';
import InstructionsBar from '@/components/InstructionsBar.vue';
import TextInstruction from '@/components/TextInstruction.vue';
import VideoInstruction from '@/components/VideoInstruction.vue';
import SummaryInstruction from '@/components/SummaryInstruction.vue';
import RecordingBar from '@/components/RecordingBar.vue';
import { Instruction } from '@/types/Instruction';
import { Record } from '@/types/Record';
import { Template } from '@/types/Template';
import { Video } from '@/types/Video';
import Vue from 'vue';
import Qrcode from 'qrcode.vue';
import { mapGetters } from 'vuex';

export default Vue.extend({
    name: 'Project',
    components: {
        InstructionsBar,
        TextInstruction,
        VideoInstruction,
        SummaryInstruction,
        RecordingBar,
        Qrcode,
    },
    data: (): {
        isReady: boolean;
        isRecording: boolean;
        stream: MediaStream | null;
        mediaRecorder: MediaRecorder | null;
        currentIndex: number;
        didRedo: boolean;
        didRepeat: boolean;
        snackbar: boolean;
        snackbarMessage: string;
        snackbarColor: string;
        qrCode: string | null;
        showQrCode: boolean;
    } => ({
        isReady: false,
        isRecording: false,
        stream: null,
        mediaRecorder: null,
        currentIndex: 0,
        didRedo: false,
        didRepeat: false,
        snackbar: false,
        snackbarMessage: '',
        snackbarColor: 'error',
        qrCode: null,
        showQrCode: false,
    }),
    computed: {
        ...mapGetters(['videos', 'templates', 'records']),
        video(): Video {
            return this.videos.find((video: Video) => video.id.toString() === this.$route.params.id);
        },
        template(): Template {
            return this.templates.find((template: Template) => template.id === this.video.template.id);
        },
        currentInstruction(): Instruction | undefined {
            return this.template.instructions[this.currentIndex] ?? undefined;
        },
        lastInstruction(): Instruction | undefined {
            return this.template.instructions[this.currentIndex - 1] ?? undefined;
        },
        windowHeight: () => window.innerHeight,
    },
    methods: {
        isCompleted(instruction: Instruction) {
            return (
                this.records.findIndex((record: Record) => {
                    if (record.instruction_id !== instruction.id) {
                        return false;
                    }

                    if (record.video_id !== this.video.id) {
                        return false;
                    }

                    if (instruction.type === 'video' && record.file_id === undefined) {
                        return false;
                    }

                    return true;
                }) !== -1
            );
        },
        close() {
            this.$router.back();
        },
        canRedo() {
            return this.currentIndex > 0 && !this.didRedo;
        },
        canRepeat() {
            return this.lastInstruction && this.lastInstruction.is_repeatable && !this.didRepeat;
        },
        canSkip() {
            const record = this.records.find((record: Record) => record.video_id === this.video.id && record.instruction_id === this.currentInstruction?.id);

            return this.currentInstruction?.is_skippable || record !== undefined;
        },
        redo() {
            if (!this.canRedo()) {
                return;
            }
            this.didRedo = true;
            this.currentIndex = Math.max(0, this.currentIndex - 1);
        },
        repeat() {
            if (!this.canRepeat()) {
                return;
            }
            this.didRepeat = true;
            this.currentIndex = Math.max(0, this.currentIndex - 1);
        },
        skip() {
            if (!this.canSkip()) {
                return;
            }
            this.next();
        },
        next() {
            this.currentIndex = Math.min(this.currentIndex + 1, this.template.instructions.length);
            this.didRedo = false;
            this.didRedo = false;
        },
        async share() {
            const instructionIds = this.template.instructions.map((instruction) => instruction.id);
            const response = await api.post(`/videos/${this.video.id}/make-invitation`, {
                instruction_ids: instructionIds.join(''),
            });

            if (response && response.data && response.data.code) {
                this.qrCode = `${location.origin}/code/${response.data.code}`;
                this.showQrCode = true;
            }
        },
        showSnackbarMessage(message: string) {
            this.snackbarMessage = message;
            this.snackbar = true;
        },
    },
    watch: {
        stream(stream: MediaStream) {
            (this.$refs.preview as HTMLVideoElement).srcObject = stream;
        },
    },
    async mounted() {
        try {
            const constraints: MediaStreamConstraints = {
                audio: true,
                video: {
                    facingMode: 'user',
                    width: 1280,
                    height: 720,
                },
            };

            if ('mediaDevices' in navigator) {
                this.stream = await navigator.mediaDevices.getUserMedia(constraints);
            } else {
                this.stream = await new Promise<MediaStream>((resolve, reject) => {
                    navigator.getUserMedia(constraints, resolve, reject);
                });
            }

            let index = 0;
            for (const instruction of this.template.instructions) {
                if (!this.isCompleted(instruction)) {
                    break;
                }
                index++;
            }
            this.currentIndex = Math.min(index, this.template.instructions.length + 1);

            this.isReady = true;
        } catch (error) {
            this.showSnackbarMessage('It seems that your browser does not support video recording. :-(');
        }
    },
    beforeDestroy(): void {
        if (this.stream) {
            for (const track of this.stream.getTracks()) {
                track.stop();
            }
        }
    },
});
